import { FilterType, InstallationScopes } from 'lib/InstallationScope'

export type ResponseObject<T> = {
  data: T
}

export type JobForInstallation = {
  jobId: string
  environment: string
  created: string
  concluded: string | null
  updatesShapeFile: boolean
  updatesBarcodeFile: boolean
  updatesCrateFile: boolean
  updatesLocalCrateFile: boolean
  updatesArtikeldatenFile: boolean
  status: string
}

export type InstallationDTO = {
  commonId?: string
  commonName?: string
  installationReference: InstallationReference
  machineConfig: null | {
    flow: boolean
    crate: boolean
    shouldRunClose: boolean
  }
  history: JobForInstallation[]
}

export type InstallationReference = {
  installationId: string
  serialNumber: string
  chainName?: string
  subChainName?: string
  locationName: string
  country: string
  region?: string
  machineFamily: string
  materialGroup: string
}

type CommonConfigFileStream = {
  id: string
  name: string
}

export type MaintenanceWindow = {
  start: string
  end: string
  tz: string
  weekdays?: number
}
export type ScopeFilterDTO = {
  scopeFilterType: FilterType
  allowedValues: string[]
}
export type ScopeFilter = {
  type: FilterType
  value: string
  commonAssortmentId?: string
  include: boolean
}
export type ScopeFilters = {
  scopeFilters: ScopeFilter[]
}
export type CommonConfig = {
  commonAssortmentId: string
  name: string
  country: string
  maintenanceWindow: MaintenanceWindow
  lockout: boolean
  concurrencyLimit: number | null
  installationScopes: InstallationScopes
  scopeFilters: ScopeFilters
  shapeFileStream: CommonConfigFileStream | null
  barcodeFileStream: CommonConfigFileStream | null
  barcode2FileStream: CommonConfigFileStream | null
  crateFileStream: CommonConfigFileStream | null
  artikeldatenFileStream: CommonConfigFileStream | null
}

export type MachineAssortmentStatus =
  | 'CREATED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'RECOVERED'
  | 'NOT_COMPATIBLE'

export type InstallationOverview = {
  installationId: string
  serialNumber: string
  chainName: string | null
  subChainName: string | null
  locationName: string
  country: string
  region: string | null
  commonAssortmentId: string | null
  commonAssortmentName: string | null
  shapeStatus: InstallationAssortmentStatus
  barcodeStatus: InstallationAssortmentStatus
  crateStatus: InstallationAssortmentStatus
  localCrateStatus: InstallationAssortmentStatus
  artikeldatenStatus: InstallationAssortmentStatus
}

export type InstallationAssortmentStatus = {
  enabledForCommonAssortment: boolean
  lastUpdateStatus: MachineAssortmentStatus | null
  lastUpdateStatusTimestamp: string | null
  pendingUpdateReason: PendingUpdateReason
}

export enum PendingUpdateReason {
  UP_TO_DATE = 'UP_TO_DATE',
  NEW_FILE_AVAILABLE = 'NEW_FILE_AVAILABLE',
  COMMON_ASSORTMENT_MODIFICATIONS = 'COMMON_ASSORTMENT_MODIFICATIONS',
  INSTALLATION_MODIFICATIONS = 'INSTALLATION_MODIFICATIONS',
}

export enum FileType {
  SHAPE = 'SHAPE',
  BARCODE = 'BARCODE',
  CRATE = 'CRATE',
  LOCAL_CRATE = 'LOCAL_CRATE',
  ARTIKELDATEN = 'ARTIKELDATEN',
  UNKOWN = 'UNKNOWN',
}

export const fileTypeDisplayName = (type: FileType): string => {
  const names: Record<FileType, string> = {
    [FileType.SHAPE]: 'Shape',
    [FileType.BARCODE]: 'Barcode',
    [FileType.CRATE]: 'Crate',
    [FileType.LOCAL_CRATE]: 'Local Crate',
    [FileType.ARTIKELDATEN]: 'Artikeldaten',
    [FileType.UNKOWN]: 'Unknown',
  }
  return names[type]
}

export type CrateFile = {
  crateGroups: CrateGroup[]
}

export type CrateGroup = {
  id: string
  name: string
  accepted: boolean
  inAssortment: boolean
  refundGroup: boolean
  minimumCrateItems: boolean
  superGroup: boolean
}

export type Modification = {
  referenceObjectId: string
  actionType: string
  actionValue: string
  allowedOverride: boolean
}

export type ShapeFile = {
  bottleGroups: BottleGroup[]
}

export type BottleGroup = {
  groupNumber: string
  superGroupNumber: number
  refundGroup: number
  returnFlag: number
  name: string
}

export type BarcodeAndSonderpfand = {
  barcodeFile: BarcodeFile
  sonderpfandGroups?: SonderpfandGroup[]
}

export type BarcodeFile = {
  barcodes: BarcodeLine[]
}

export type SonderpfandGroup = {
  id: string
  name: string
  barcodes: string[]
}

export type BarcodeLine = {
  code: string
  property?: { key: number; value: number }
  sortCode: number
  flags: number
  comment?: string
}

export type ArtikeldatenFile = {
  content: ArtikeldatenGroup[]
}

export type ArtikeldatenGroup = {
  articleNumber: string
  validFrom: string
  validTo: string
}

export type ValueDiff = {
  newValue: Dictionary
  oldValue: Dictionary
}

export type KeyValue = {
  key: string
  value: string
}

export type Value = {
  value: string
}

export function isKeyValue(value: unknown): value is KeyValue {
  if ((value as KeyValue).key) {
    return true
  }
  return false
}

export function isValue(value: unknown): value is Value {
  if ((value as Value).value) {
    return true
  }
  return false
}

export type Dictionary = { [key: string]: number | string | KeyValue | undefined }

export type ListDiff = {
  added: Dictionary[]
  updated: ValueDiff[]
  removed: Dictionary[]
}

export type Diff = ValueDiff | ListDiff

export type DiffMap = {
  [key: string]: Diff
}

export type StagedFile = {
  storedObjectPath: string
  parsedVersion: string
  latestFileId: number
}

export type ValidationMessage = {
  severity: 'WARNING' | 'ERROR'
  entityId: string | null
  message: string
}

export type FileUploadResult = {
  stagedFile: StagedFile | null
  diff: DiffMap | null
  messages: ValidationMessage[]
}

export type FileSummary = {
  parsedVersion: string
  fileName: string
  created: Date
  createdBy: string
  url: string | null
}

export type CommonReference = {
  commonId: string
  name: string
  installationScope: InstallationScopes
}

export type FileStreamReference = {
  streamId: string
  name: string
  fileType: 'BARCODE' | 'SHAPE' | 'CRATE' | 'ARTIKELDATEN'
  commonAssortmentReferences: Array<CommonReference>
}

export type FileStreamWithHistory = FileStreamReference & {
  current: FileSummary | null
  history: FileSummary[]
}

export type CommonsWithAssortmentConflicts = {
  commons: CommonAssortmentWithCountForStatusTypes[]
  commonAssortmentConflicts: CommonAssortmentConflict[]
}

export type CommonAssortmentWithCountForStatusTypes = {
  commonAssortmentId: string
  name: string
  country: string
  maintenanceWindow: MaintenanceWindow
  concurrencyLimit?: number
  installationScopes: InstallationScopes
  scopeFilters: ScopeFilters
  lockout: boolean
  shapeFileStream?: CommonConfigFileStream | null
  barcodeFileStream?: CommonConfigFileStream | null
  barcode2FileStream?: CommonConfigFileStream | null
  crateFileStream?: CommonConfigFileStream | null
  artikeldatenFileStream?: CommonConfigFileStream | null
  statusTypes: Record<string, number>
  installationCount: number
}

export type Installation = {
  installationId: string
  serialNumber: string
  chainId: string | null
  chainName: string | null
  subChainId: string | null
  subChainName: string | null
  sourceSystem: string
  locationId: string | null
  locationName: string
  country: string
  region: string | null
  status: string | null
  clearingHouse: string | null
  commonAssortmentId: string | null
  machineFamily: string
  materialGroup: string
}

export type CommonAssortmentConflict = {
  installation: Installation
  conflictingCommonAssortments: CommonAssortmentSummary[]
}

export type CommonAssortmentSummary = {
  commonAssortmentId: string
  name: string
}

export type ToleranceGroupDTO = {
  commonId: string
  toleranceGroupId: number | null
  minHeight: number | null
  maxHeight: number | null
  minDiameter: number | null
  maxDiameter: number | null
}

export type ToleranceGroupSummaryDTO = {
  toleranceGroupId: number
  toleranceGroupsFromCpg: ToleranceGroupDTO[]
  toleranceGroupsFromDb: ToleranceGroupDTO[]
}
